import { useEffect, useState } from "react";
import { ApiError } from "../lib/api.ts";
import { loadAppConfig } from "../lib/appConfig.ts";
import { storeBilling } from "../lib/storeBilling.ts";
import { useSession } from "../lib/session.ts";
import { messages } from "../i18n/index.ts";

export function RemoveAdsCard() {
  const user = useSession((state) => state.user);
  const refreshStats = useSession((state) => state.refreshStats);
  const [enabled, setEnabled] = useState(false);
  const [price, setPrice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const billing = storeBilling();

  useEffect(() => {
    if (!billing) return;
    let active = true;
    void loadAppConfig().then(async (config) => {
      if (!active) return;
      setEnabled(Boolean(config.purchasesEnabled));
      if (config.purchasesEnabled) {
        try {
          const formatted = await billing.price();
          if (active) setPrice(formatted);
        } catch { /* The store is unavailable; buying stays disabled. */ }
      }
    });
    const external = billing.onExternalPurchase(() => { void refreshStats(); });
    return () => {
      active = false;
      void external.then((handle) => handle?.remove());
    };
  }, [billing, refreshStats]);

  if (!billing || !user) return null;
  const store = billing;

  function failure(error: unknown): string {
    return error instanceof ApiError && error.status === 409
      ? messages.profile.purchaseWrongAccount : messages.profile.purchaseFailed;
  }

  async function handlePurchase() {
    setBusy(true); setNotice("");
    try {
      const outcome = await store.purchase();
      if (outcome === "cancelled") return;
      await refreshStats();
      setNotice(outcome === "active" ? messages.profile.purchaseOwned : messages.profile.purchasePending);
    } catch (error) {
      setNotice(failure(error));
    } finally { setBusy(false); }
  }

  async function handleRestore() {
    setBusy(true); setNotice("");
    try {
      const outcome = await store.restore();
      if (outcome === "none") { setNotice(messages.profile.purchaseUnavailable); return; }
      await refreshStats();
      setNotice(outcome === "active" ? messages.profile.purchaseOwned : messages.profile.purchasePending);
    } catch (error) {
      setNotice(failure(error));
    } finally { setBusy(false); }
  }

  return <section className="flex flex-col gap-2">
    <h3 className="section-title">{messages.profile.removeAds}</h3>
    <p className="text-sm text-muted">{messages.profile.removeAdsDescription}</p>
    {user.removeAds ? <p className="text-sm text-yellow-400">★ {messages.profile.purchaseOwned}</p> : <>
      {price && <p className="text-sm font-bold">{price}</p>}
      <button type="button" className="btn-secondary" disabled={!enabled || !price || busy} onClick={() => { void handlePurchase(); }}>{messages.profile.buy}</button>
      {!enabled && <p className="text-xs text-muted">{messages.profile.purchaseUnavailable}</p>}
    </>}
    <button type="button" className="btn-secondary" disabled={busy} onClick={() => { void handleRestore(); }}>{messages.profile.restore}</button>
    {notice && <p role="status" className="text-sm text-muted">{notice}</p>}
  </section>;
}
